import React, { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../firebase';
import { Helmet } from 'react-helmet';
import Newsletter from '../../components/Newsletter/Newsletter';
import NewsletterPopup from '../../components/NewsletterPopup/NewsletterPopup';

const Blog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [latestArticle, setLatestArticle] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  useEffect(() => {
    const articlesRef = query(
      collection(db, "articles"),
      where("isVisible", "==", true),
      orderBy("createdAt", "desc")
    );

    const unsubscribeArticles = onSnapshot(articlesRef, (snapshot) => {
      setArticles(snapshot.docs.map(doc => ({
        id: doc.id,
        data: doc.data()
      })));
    });

    const latestArticleRef = query(
      collection(db, "articles"),
      where("isVisible", "==", true),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const unsubscribeLatestArticle = onSnapshot(latestArticleRef, (snapshot) => {
      if (!snapshot.empty) {
        const latestDoc = snapshot.docs[0];
        setLatestArticle({ id: latestDoc.id, data: latestDoc.data() });
      } else {
        setLatestArticle(null);
      }
    });

    const categoriesRef = query(collection(db, "categories"));

    const unsubscribeCategories = onSnapshot(categoriesRef, (snapshot) => {
      setCategories(snapshot.docs.map(doc => ({
        id: doc.id,
        data: doc.data()
      })));
    });

    return () => {
      unsubscribeArticles();
      unsubscribeLatestArticle();
      unsubscribeCategories();
    };
  }, []);

  const handleSearch = async (e) => {
    try {
      e.preventDefault();
      setIsSearchLoading(true);
      const searchArticlesRef = query(
        collection(db, 'articles'),
        where('titleLowerCase', '>=', searchTerm.toLowerCase()),
        where('isVisible', '==', true)
      );

      onSnapshot(searchArticlesRef, (snapshot) => {
        setSearchResults(snapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        })));
      });
    } catch (error) {
      console.error('Error searching articles:', error);
    }
  };

  const endSearch = () => {
    setIsSearchLoading(false);
    setSearchTerm('');
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const filteredArticles = selectedCategory === "ALL"
    ? articles
    : articles.filter(article => article.data.rubriques?.includes(selectedCategory));

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: "beforeChildren",
        staggerChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (

    <>
      <Helmet>
        <title>WEAREBIAM | MEDIA</title>
        <meta
          name="description"
          content="Plongez dans l'univers de WEAREBIAM : interviews, reportages, behind the scenes et contenus exclusifs pour inspirer une nouvelle génération de créatifs."
        />

        {/* Open Graph */}
        <meta property="og:title" content="WEAREBIAM | MEDIA" />
        <meta
          property="og:description"
          content="Interviews, reportages, behind the scenes et contenus exclusifs pour inspirer une nouvelle génération de créatifs."
        />
        <meta property="og:image" content="https://firebasestorage.googleapis.com/v0/b/werebiam.firebasestorage.app/o/assets%2Fwebcover_bw.png?alt=media&token=f3686ce5-b347-4f99-8b23-0f52d461675c" />
        <meta property="og:url" content="https://wearebiam.com/media" />
        <meta property="og:type" content="website" />
      </Helmet>


      <div className="flex flex-col items-center justify-center h-full xl:gap-[100px] gap-[20px] lg:p-[5rem] lg:pb-[100px] pb-[80px] bg-black w-full">
        <div className="flex flex-col bg-black xl:max-w-[1440px] gap-[50px] xl:gap-0 h-full w-full">

          <section className="flex flex-col w-full lg:h-[80dvh] gap-0 px-[1.25rem] lg:mt-[20px] mt-[100px]">
            {latestArticle ? (
              <motion.div
                className="relative flex items-center lg:px-[5rem] p-[1.25rem] w-full lg:h-[60%] bg-cover bg-center rounded-lg transition-all duration-700 ease-in-out"
                style={{ backgroundImage: `url(${latestArticle.data.coverUrl})` }}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-md rounded-lg"></div>

                <div className="flex lg:flex-row lg:h-[80%] flex-col lg:px-[1.25rem] lg:justify-start justify-center lg:items-center items-start gap-10 relative z-10 w-full">
                  <div className="lg:w-[4/10] lg:block hidden w-auto aspect-[4/5] lg:h-full rounded-lg bg-center bg-cover"
                    style={{ backgroundImage: `url(${latestArticle.data.coverUrl})` }} />

                  <div className="w-full lg:hidden block aspect-[4/5] lg:h-full rounded-lg bg-center bg-cover"
                    style={{ backgroundImage: `url(${latestArticle.data.coverUrl})` }} />

                  <div className="flex flex-col gap-5 lg:w-[6/10]">
                    <motion.div className="lg:flex grid grid-cols-3 w-full gap-[20px] overflow-x-scroll" variants={containerVariants}>
                      {Array.isArray(latestArticle?.data.rubriques) && latestArticle.data.rubriques.length > 0 ? (
                        latestArticle.data.rubriques.map((rubrique, index) => (
                          <motion.div
                            key={index}
                            className="flex items-center justify-center lg:text-[16px] text-[14px] text-black bg-yellow ppmori font-bold rounded-md px-5 py-1"
                            variants={itemVariants}
                          >
                            {rubrique}
                          </motion.div>
                        ))
                      ) : (
                        <p className="text-white">---</p>
                      )}
                    </motion.div>

                    <motion.p className="text-white lg:text-[30px] text-[18px] ppmori font-bold" variants={itemVariants}>
                      {latestArticle.data.title}
                    </motion.p>

                    <motion.div className="flex w-full" variants={itemVariants}>
                      <Link to={`/media/${latestArticle.id}`} className='bg-white text-black hover:text-white hover:bg-black text-[12px] rounded-md px-2.5 py-1 w-fit transition-all'>lire maintenant</Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ) :

              <div
                className="relative flex items-center lg:px-[5rem] p-[1.25rem] w-full lg:h-[60%] bg-cover bg-center rounded-lg transition-all duration-700 ease-in-out shadow-xl"
              >

                {/* Filtre noir semi-transparent + flou */}
                <div className="absolute inset-0 bg-lightgrey3 bg-opacity-50 backdrop-blur-md rounded-lg"></div>

                {/* Contenu de ta div */}
                <div className="flex lg:flex-row lg:h-[80%] flex-col justify-center items-center gap-10 relative z-10 w-full">
                  <div className="lg:w-[4/10] lg:block hidden w-auto aspect-[4/5] lg:h-full rounded-lg bg-center bg-cover bg-black shadow-xl"
                  />

                  <div className="flex flex-col gap-5 lg:w-[6/10]">

                    <div className="flex gap-[20px] overflow-x-scroll">

                      <div className="flex items-center justify-center text-black bg-yellow ppmori font-bold rounded-md px-5 py-1">

                      </div>

                    </div>

                    <p className="flex text-lightgrey3 text-[28px] ppmori font-bold">
                      **************** <span className='lg:block hidden'>*******************************</span>
                    </p>

                    <div className="flex w-full">
                      <Link to={"/media"} className='bg-white text-black hover:text-white hover:bg-black text-[12px] rounded-md px-2.5 py-1 w-fit'></Link>
                    </div>

                  </div>
                </div>

              </div>

            }

            <motion.div className="flex flex-col ppmori mt-[100px]" variants={containerVariants} initial="hidden" animate="visible">
              <p className="lg:text-[34px] text-[24px] text-white font-bold">Le Média</p>
              <p className="lg:text-[14px] text-[16px] text-white mb-5">Ici on analyse, on écrit et on partage.</p>

              <motion.div className="lg:flex grid grid-cols-3 gap-5 overflow-x-scroll" variants={containerVariants}>
                <motion.button
                  className={`lg:text-[12px] text-[14px] border rounded-md font-thin border-white px-3 py-1 ${selectedCategory === "ALL" ? "bg-white text-black" : "text-white bg-black"}`}
                  onClick={() => handleCategoryClick("ALL")}
                  variants={itemVariants}
                >
                  All
                </motion.button>
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    className={`lg:text-[12px] text-[14px] border rounded-md font-thin border-white px-3 py-1 ${selectedCategory === category.data.nom ? "bg-white text-black" : "text-white bg-black"}`}
                    onClick={() => handleCategoryClick(category.data.nom)}
                    variants={itemVariants}
                  >
                    {category.data.nom}
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          </section>

          <motion.section
            className='grid lg:grid-cols-4 lg:gap-5 gap-5 grid-cols-2 px-[1.25rem] w-full'
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article, index) => (
                <motion.div key={index} variants={itemVariants}>
                  <Link to={`/media/${article.id}`} className="flex relative w-full aspect-[4/5] bg-darkgrey rounded-lg bg-cover bg-no-repeat bg-center"
                    style={{ backgroundImage: `url(${article.data.coverUrl})` }}>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="flex justify-center w-full font-agrandir xl:text-[18px] text-[15px]">
                <div className="flex flex-col text-white aspect-[4/5]">
                  Aucun article trouvé pour cette catégorie.
                  <span className="h-1 w-14 bg-tangerine" />
                </div>
              </div>
            )}
          </motion.section>

          <NewsletterPopup />

          <Newsletter />

        </div>
      </div>

    </>
  );
};

export default Blog;
